// src/pages/DevHub/PaymentMock/PaymentMockPage.tsx
/**
 * Refined for premium, marketing-ready aesthetic.
 * Mirrors the visual polish of the antigravity.google Features page.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
    CreditCard, Trash2, Search, Info, RefreshCw, Pause, Play,
    Clock, Send, Check, Copy, CheckCircle, ArrowRight,
    Zap, AlertTriangle, ShieldCheck, ExternalLink, Webhook,
    Shield, Lock, Code2, Users,
} from 'lucide-react';
import SEO from '../../../components/SEO/SEO';
import CodeBlock from '../../../components/CodeBlock/CodeBlock';
import { DevStatusPill } from '../components';
import type { DevStatusVariant } from '../components';
import { Typography } from '../../../components/ui/typography';
import { m, type MotionProps } from 'framer-motion';

// ── Types ─────────────────────────────────────────────────────────────────────

interface TimelineEvent { event: string; message: string; ts: number; }
interface PaymentIntent {
    id: string; amount: number; currency: string; status: string; created: number;
    description: string; webhook_url?: string; metadata?: Record<string, any>;
    timeline?: TimelineEvent[];
}
type LanguageTab = 'javascript' | 'python' | 'nodejs';

// ── Design tokens ─────────────────────────────────────────────────────────────

const A  = '#f9ab00';          // accent
const AB = '#fef7e0';          // accent bg
const AD = '#b45309';          // accent dark

// ── Animation ─────────────────────────────────────────────────────────────────

const fu = (delay = 0): MotionProps => ({
    initial: { opacity: 0, y: 28 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.55, delay, ease: [0.21, 0.45, 0.32, 0.9] as any },
});

// ── Shared card class ─────────────────────────────────────────────────────────

const CARD = 'bg-white border border-[#e8eaed] rounded-[32px] overflow-hidden hover:shadow-lg transition-shadow duration-300';

// ── FactChip ──────────────────────────────────────────────────────────────────

const FactChip: React.FC<{ label: string; value: string }> = ({ label, value }) => (
    <div className="flex flex-col px-4 py-2.5 rounded-2xl border border-[#e8eaed] bg-[#f8f9fa]">
        <span className="text-[0.7rem] font-medium text-[#80868b] uppercase tracking-wide">{label}</span>
        <span className="text-[0.9rem] font-medium text-[#121317]">{value}</span>
    </div>
);

// ── SectionHeader — matches FeaturesPage exactly ──────────────────────────────

const SH: React.FC<{
    icon: React.ElementType; ic: string; ib: string;
    eyebrow: string; title: string; subtitle: string;
    seeAll?: string; seeAllLabel?: string;
}> = ({ icon: Icon, ic, ib, eyebrow, title, subtitle, seeAll, seeAllLabel }) => (
    <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 mb-12">
        <div className="max-w-4xl">
            <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: ib, color: ic }}>
                    <Icon size={18} strokeWidth={1.5} />
                </div>
                <span className="text-[0.8rem] font-semibold uppercase tracking-widest" style={{ color: ic }}>{eyebrow}</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-light text-[#121317] mb-3">{title}</h2>
            <p className="text-[#5f6368] text-lg leading-relaxed">{subtitle}</p>
        </div>
        {seeAll && seeAllLabel && (
            <Link to={seeAll} className="flex items-center gap-1.5 font-medium text-[0.95rem] no-underline hover:underline shrink-0 group" style={{ color: ic }}>
                {seeAllLabel} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
        )}
    </div>
);

// ── DeepDive card — identical to FeaturesPage ─────────────────────────────────

interface DDCardProps {
    icon: React.ElementType; title: string; tagline: string; description: string;
    useCases: { label?: string; text: string }[];
    keyFacts: { label: string; value: string }[];
    cta: string; ctaAction?: () => void; ctaTo?: string;
    ac: string; ab: string; flip?: boolean; index?: number;
    useCasesTitle?: string;
}

const DDCard: React.FC<DDCardProps> = ({
    icon: Icon, title, tagline, description, useCases, keyFacts,
    cta, ctaAction, ctaTo, ac, ab, flip = false, index = 0, useCasesTitle = 'Common use cases',
}) => (
    <m.div
        initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }} transition={{ duration: 0.55, delay: (index % 2) * 0.08 }}
        className={CARD}
    >
        <div className={`grid grid-cols-1 lg:grid-cols-2`}>
            {/* Content side */}
            <div className={`p-8 md:p-12 flex flex-col justify-between ${flip ? 'order-1 lg:order-2' : ''}`}>
                <div>
                    <div className="flex items-start gap-4 mb-6">
                        <div className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 mt-0.5" style={{ backgroundColor: ab, color: ac }}>
                            <Icon size={24} strokeWidth={1.5} />
                        </div>
                        <div>
                            <h3 className="text-2xl font-medium text-[#121317] leading-snug">{title}</h3>
                            <p className="text-[0.9rem] mt-1" style={{ color: ac }}>{tagline}</p>
                        </div>
                    </div>
                    <p className="text-[0.9375rem] text-[#5f6368] leading-[1.75] mb-8">{description}</p>
                    <div className="flex flex-wrap gap-3 mb-8">
                        {keyFacts.map(f => <FactChip key={f.label} label={f.label} value={f.value} />)}
                    </div>
                    {ctaTo ? (
                        <Link to={ctaTo}
                            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full font-medium text-white no-underline transition-all hover:opacity-90 hover:shadow-md text-[0.9375rem] relative overflow-hidden group"
                            style={{ backgroundColor: ac }}>
                            {cta} <ArrowRight size={16} />
                            <div className="absolute top-0 left-[-100%] w-full h-full bg-gradient-to-r from-transparent via-white/20 to-transparent transition-all duration-500 group-hover:left-[100%]" />
                        </Link>
                    ) : (
                        <button onClick={ctaAction}
                            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full font-medium text-white cursor-pointer border-none transition-all hover:opacity-90 hover:shadow-md text-[0.9375rem] relative overflow-hidden group"
                            style={{ backgroundColor: ac }}>
                            {cta} <ArrowRight size={16} />
                            <div className="absolute top-0 left-[-100%] w-full h-full bg-gradient-to-r from-transparent via-white/20 to-transparent transition-all duration-500 group-hover:left-[100%]" />
                        </button>
                    )}
                </div>
            </div>
            {/* Use cases panel */}
            <div className={`p-8 md:p-12 flex flex-col justify-center ${flip ? 'order-2 lg:order-1' : ''}`} style={{ backgroundColor: ab + '55' }}>
                <h4 className="text-[0.75rem] font-semibold uppercase tracking-widest mb-5" style={{ color: ac }}>{useCasesTitle}</h4>
                <ul className="space-y-4 list-none p-0 m-0">
                    {useCases.map((uc, i) => (
                        <li key={i} className="flex items-start gap-3">
                            <CheckCircle size={16} className="shrink-0 mt-0.5" style={{ color: ac }} />
                            <span className="text-[0.9rem] text-[#3c4043] leading-snug">
                                {uc.label
                                    ? <><code className="text-[0.8em] font-mono text-[#121317] bg-white border border-[#e8eaed] px-1.5 py-0.5 rounded-lg">{uc.label}</code><span className="ml-1">— {uc.text}</span></>
                                    : uc.text}
                            </span>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    </m.div>
);

// ── Platform Qualities — matches FeaturesPage ─────────────────────────────────

const PlatformQualities: React.FC = () => (
    <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-6">
            <div className="bg-white rounded-[32px] border border-[#e8eaed] p-8 md:p-16 grid grid-cols-1 md:grid-cols-3 gap-12 text-center">
                {[
                    { icon: Shield, color: A, bg: AB,
                      title: 'Zero Financial Risk',
                      desc: 'No real cards, no real charges, no gateway account needed. Every intent, webhook, and outcome is 100% synthetic — safe to run as many times as you want.' },
                    { icon: Zap, color: '#1a73e8', bg: '#e8f0fe',
                      title: 'Real Webhook Dispatch',
                      desc: 'Live HTTP POST calls fire to your endpoint on every lifecycle event — exactly like a production gateway — so your handlers are tested against real payloads.' },
                    { icon: Lock, color: '#1e8e3e', bg: '#e6f4ea',
                      title: 'Stateless & Private',
                      desc: 'Sessions are ephemeral and keyed to your browser. No data is stored on our servers after your session ends. Nothing leaves your browser without your intent.' },
                ].map((q, i) => {
                    const Icon = q.icon;
                    return (
                        <m.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.1 }} className="flex flex-col items-center">
                            <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6 shadow-sm" style={{ backgroundColor: q.bg }}>
                                <Icon size={26} style={{ color: q.color }} />
                            </div>
                            <h3 className="text-xl font-medium text-[#121317] mb-3">{q.title}</h3>
                            <p className="text-[#5f6368] leading-relaxed max-w-xs">{q.desc}</p>
                        </m.div>
                    );
                })}
            </div>
        </div>
    </section>
);

// ── Why section — matches "Why FileFusion?" pattern exactly ───────────────────

const WhySection: React.FC = () => (
    <section className="max-w-7xl mx-auto px-6 py-20 border-t border-[#f1f3f4]">
        <div className="mb-12">
            <h2 className="text-3xl md:text-4xl font-light text-[#121317] mb-3">Why use the Payment Simulator?</h2>
            <p className="text-[#5f6368] text-lg">Three reasons developers ship faster with it</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
                { icon: Users, color: '#1e8e3e', bg: '#e6f4ea',
                  title: 'No Gateway Account',
                  desc: 'Test the entire payment flow without signing up for Stripe, PayPal, or any gateway. No API keys, no test mode credentials, no billing.' },
                { icon: Shield, color: A, bg: AB,
                  title: 'Trigger Any Failure',
                  desc: 'Instantly simulate declines, fraud flags, network timeouts, and partial refunds on demand. Cover every edge case before you write a single handler.' },
                { icon: Zap, color: '#1a73e8', bg: '#e8f0fe',
                  title: 'End-to-End Webhook Chain',
                  desc: 'Webhooks fire to your real server — not a mock. See the exact payload, headers, and timing your handler will receive in production.' },
            ].map((c, i) => {
                const Icon = c.icon;
                return (
                    <m.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }}
                        className="bg-white border border-[#e8eaed] rounded-[32px] p-8">
                        <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-6" style={{ backgroundColor: c.bg }}>
                            <Icon size={22} style={{ color: c.color }} />
                        </div>
                        <h3 className="text-xl font-medium text-[#121317] mb-3">{c.title}</h3>
                        <p className="text-[#5f6368] leading-relaxed">{c.desc}</p>
                    </m.div>
                );
            })}
        </div>
    </section>
);

// ── Bottom CTA — matches FeaturesPage FAQ CTA exactly ────────────────────────

const BottomCTA: React.FC = () => (
    <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-12 bg-white border border-[#e8eaed] rounded-[32px] p-8 md:p-16">
                <div className="max-w-xl text-center md:text-left">
                    <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-6 mx-auto md:mx-0" style={{ backgroundColor: AB, color: A }}>
                        <CreditCard size={24} />
                    </div>
                    <h2 className="text-3xl font-light text-[#121317] mb-4">Ready to build your integration?</h2>
                    <p className="text-[#5f6368] text-lg leading-relaxed">
                        Create a free sandbox in seconds. Test your entire payment flow — no accounts, no risk, no time limit.
                    </p>
                </div>
                <div className="flex flex-col items-center gap-4 shrink-0">
                    <Link to="/dev-hub" className="flex items-center gap-2 px-6 py-2.5 rounded-full font-medium text-white no-underline transition-colors shadow-sm" style={{ backgroundColor: A }}>
                        Explore Developer Hub
                    </Link>
                    <div className="flex flex-wrap gap-3 justify-center">
                        {[
                            { to: '/dev-hub/webhook-tester', label: 'Webhook Tester' },
                            { to: '/dev-hub/mock-api', label: 'Mock API' },
                            { to: '/features', label: 'All Features' },
                        ].map(l => (
                            <Link key={l.to} to={l.to} className="text-sm font-medium text-[#5f6368] hover:text-[#121317] px-4 py-2 bg-[#f8f9fa] rounded-full border border-[#dadce0] transition-colors no-underline">
                                {l.label}
                            </Link>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    </section>
);

// ── Helper ────────────────────────────────────────────────────────────────────

const statusVariant = (s: string): DevStatusVariant => {
    if (s === 'succeeded') return 'success';
    if (s === 'requires_confirmation') return 'warning';
    if (s === 'declined') return 'failed';
    if (s === 'processing') return 'processing';
    return 'pending';
};

// ── Page ──────────────────────────────────────────────────────────────────────

const PaymentMockPage: React.FC = () => {
    const apiBase = (import.meta as any).env.VITE_API_URL || 'http://localhost:8000';
    const [clientId, setClientId] = useState('');
    const [intents, setIntents] = useState<PaymentIntent[]>([]);
    const [activeIntent, setActiveIntent] = useState<PaymentIntent | null>(null);
    const [isPolling, setIsPolling] = useState(true);
    const [copiedUrl, setCopiedUrl] = useState(false);
    const [copiedSnippet, setCopiedSnippet] = useState(false);
    const [copiedInspectorUrl, setCopiedInspectorUrl] = useState(false);
    const [copiedMetadata, setCopiedMetadata] = useState(false);
    const [isSendingTest, setIsSendingTest] = useState(false);
    const [dispatchStatus, setDispatchStatus] = useState<'success' | 'error' | null>(null);
    const [confirming, setConfirming] = useState(false);
    const [prevCount, setPrevCount] = useState(0);
    const [activeTab, setActiveTab] = useState<LanguageTab>('javascript');
    const [isTabVisible, setIsTabVisible] = useState(true);
    const [pollInterval, setPollInterval] = useState(3000);
    const [searchTerm, setSearchTerm] = useState('');

    const filtered = Array.isArray(intents) ? intents.filter(i => {
        const q = searchTerm.toLowerCase();
        return i.id.toLowerCase().includes(q) || i.status.toLowerCase().includes(q) ||
            i.amount.toString().includes(q) || i.currency.toLowerCase().includes(q);
    }) : [];

    useEffect(() => {
        const fn = () => setIsTabVisible(document.visibilityState === 'visible');
        document.addEventListener('visibilitychange', fn);
        return () => document.removeEventListener('visibilitychange', fn);
    }, []);

    useEffect(() => {
        const id = localStorage.getItem('ff_dev_id') || localStorage.getItem('ff_webhook_id');
        if (id) { setClientId(id); if (!localStorage.getItem('ff_dev_id')) localStorage.setItem('ff_dev_id', id); }
    }, []);

    const createSandbox = () => {
        const id = Math.random().toString(36).substring(2, 12);
        localStorage.setItem('ff_dev_id', id);
        setClientId(id);
    };

    const createUrl = `${apiBase}/api/payment-simulator/intent/${clientId}`;

    const fetchHistory = useCallback(async () => {
        if (!clientId) return;
        try {
            const res = await fetch(`${apiBase}/api/payment-simulator/history/${clientId}`);
            if (!res.ok) { if (res.status === 404) return; throw new Error(); }
            const data: PaymentIntent[] = await res.json();
            if (!Array.isArray(data)) return;
            setPollInterval(data.length > prevCount ? 3000 : c => Math.min(c + 3000, 30000));
            setPrevCount(data.length);
            setIntents(data);
            setActiveIntent(cur => cur ? data.find(i => i.id === cur.id) || cur : null);
        } catch {}
    }, [clientId, apiBase, prevCount]);

    useEffect(() => { if (clientId) fetchHistory(); }, [clientId, fetchHistory]);
    useEffect(() => {
        let t: NodeJS.Timeout;
        if (isPolling && isTabVisible && clientId) t = setInterval(fetchHistory, pollInterval);
        return () => clearInterval(t!);
    }, [isPolling, isTabVisible, clientId, fetchHistory, pollInterval]);

    const handleConfirm = async (status: string, delay = 0) => {
        if (!activeIntent || confirming) return;
        setConfirming(true);
        try {
            await fetch(`${apiBase}/api/payment-simulator/confirm/${activeIntent.id}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'x-mock-status': status, 'x-mock-delay': delay.toString() },
                body: JSON.stringify({ client_id: clientId }),
            });
            await fetchHistory();
        } catch {}
        finally { setConfirming(false); }
    };

    const clearHistory = async () => {
        if (!window.confirm('Clear all payment history in this sandbox?')) return;
        try {
            await fetch(`${apiBase}/api/payment-simulator/history/${clientId}`, { method: 'DELETE' });
            setIntents([]); setActiveIntent(null);
        } catch {}
    };

    const defaultPayload = {
        amount: 2500, currency: 'USD', description: 'Premium Plan Upgrade',
        webhook_url: `${apiBase}/api/webhook-echo/${clientId}`,
        metadata: { user_id: 'u_987', type: 'test_payment' },
    };

    const getSnippet = (lang: LanguageTab) => {
        const j = JSON.stringify(defaultPayload, null, 2);
        if (lang === 'javascript') return `fetch('${createUrl}', {\n  method: 'POST',\n  headers: { 'Content-Type': 'application/json' },\n  body: JSON.stringify(${j})\n})\n.then(r => r.json())\n.then(d => console.log("Intent ID:", d.id));`;
        if (lang === 'python') return `import requests\nurl = "${createUrl}"\npayload = ${j.replace(/true/g,'True').replace(/false/g,'False').replace(/null/g,'None')}\nprint(requests.post(url, json=payload).json()['id'])`;
        if (lang === 'nodejs') return `const axios = require('axios');\naxios.post('${createUrl}', ${j})\n.then(r => console.log(r.data.id));`;
        return '';
    };

    const sendTest = async () => {
        if (isSendingTest) return;
        setIsSendingTest(true); setDispatchStatus(null);
        try {
            const res = await fetch(createUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(defaultPayload) });
            setDispatchStatus(res.ok ? 'success' : 'error');
            if (res.ok) fetchHistory();
        } catch { setDispatchStatus('error'); }
        finally { setIsSendingTest(false); setTimeout(() => setDispatchStatus(null), 3000); }
    };

    const fmt = (amt: number, curr: string) =>
        new Intl.NumberFormat('en-US', { style: 'currency', currency: curr || 'USD' }).format(amt / 100);

    // ── Landing ───────────────────────────────────────────────────────────────
    if (!clientId) {
        return (
            <div className="min-h-screen bg-white font-brand text-[#202124]">
                <SEO title="Payment Simulator | FileFusion Developer Hub" description="Simulate payment gateway behaviour with full intent lifecycle and webhook support. Zero financial risk." />

                {/* Hero */}
                <header className="max-w-7xl mx-auto px-6 pt-16 pb-20 text-center">
                    <m.div {...fu(0)}>
                        <Typography variant="hero-title" as="h1" weight="light" className="mb-6 text-[#121317]">
                            Simulate payments.{' '}<span style={{ color: A }}>Ship with confidence.</span>
                        </Typography>
                        <Typography variant="hero-subtitle" weight="normal" className="max-w-2xl mx-auto text-[#5f6368] mb-10">
                            A full-lifecycle payment sandbox — trigger intents, fire webhooks, and test every failure mode without touching a real card or gateway account.
                        </Typography>
                        <div className="flex flex-wrap gap-4 justify-center">
                            <button onClick={createSandbox}
                                className="inline-flex items-center gap-2 px-7 py-3 font-medium rounded-full text-white shadow-sm cursor-pointer border-none transition-all hover:opacity-90 hover:shadow-md relative overflow-hidden group"
                                style={{ backgroundColor: A }}>
                                <CreditCard size={17} /> Create My Sandbox <ArrowRight size={17} />
                                <div className="absolute top-0 left-[-100%] w-full h-full bg-gradient-to-r from-transparent via-white/20 to-transparent transition-all duration-500 group-hover:left-[100%]" />
                            </button>
                            <Link to="/dev-hub" className="inline-flex items-center gap-2 px-7 py-3 bg-white border border-[#dadce0] text-[#3c4043] font-medium rounded-full no-underline hover:bg-[#f8f9fa] transition-colors">
                                See Developer Hub
                            </Link>
                        </div>
                    </m.div>
                </header>

                {/* Stat strip */}
                <section className="max-w-7xl mx-auto px-6 pb-20 border-b border-[#f1f3f4]">
                    <div className="flex justify-center gap-12 sm:gap-24 flex-wrap">
                        {[
                            { value: '5+', label: 'Payment Scenarios' },
                            { value: '∞',  label: 'Webhook Events' },
                            { value: '0',  label: 'Real Money at Risk' },
                            { value: '100%', label: 'Stateless & Private' },
                        ].map(s => (
                            <div key={s.label} className="flex flex-col items-center text-center">
                                <div className="text-[2.75rem] font-light text-[#121317] leading-none mb-2">{s.value}</div>
                                <div className="text-[0.875rem] text-[#5f6368]">{s.label}</div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Payment Simulator deep-dives — white bg section */}
                <section className="max-w-7xl mx-auto px-6 py-20">
                    <SH icon={CreditCard} ic={A} ib={AB}
                        eyebrow="Payment Simulator"
                        title="Test every checkout scenario"
                        subtitle="Model fraud, timeouts, declines, and refund flows against a stateless sandbox that mirrors real production behaviour."
                        seeAll="/dev-hub" seeAllLabel="Explore Developer Hub"
                    />
                    <div className="flex flex-col gap-6">
                        <DDCard
                            icon={CreditCard}
                            title="Payment Intent Lifecycle"
                            tagline="Full-lifecycle sandbox — zero financial risk"
                            description="Simulate the complete payment intent lifecycle — create, confirm, fail, refund — in a stateless sandbox that dispatches realistic webhook events to your server. Trigger fraud scenarios, network timeouts, and card decline codes on demand. Build and harden your checkout flow without touching a test account or risking real transactions."
                            useCases={[
                                { text: 'Test how your order page handles a card decline gracefully' },
                                { text: 'Verify your refund logic fires the correct webhook handler' },
                                { text: 'Simulate a payment network timeout and check retry behaviour' },
                                { text: 'Load-test checkout without generating real payment records' },
                            ]}
                            keyFacts={[
                                { label: 'Scenarios', value: 'Fraud · timeout · decline' },
                                { label: 'Webhooks', value: 'Auto-dispatched' },
                                { label: 'Risk', value: 'Zero' },
                            ]}
                            cta="Create My Sandbox" ctaAction={createSandbox}
                            ac={A} ab={AB} index={0}
                        />
                        <DDCard
                            icon={Webhook} flip
                            title="Webhook Integration"
                            tagline="End-to-end event testing against your real server"
                            description="Every payment lifecycle event fires a real HTTP POST to your server — inspect and debug in real time. Use alongside Webhook Echo to view the exact payload, headers, and timing your handler receives. Covers every Stripe-compatible event type across the full intent lifecycle."
                            useCasesTitle="What gets dispatched"
                            useCases={[
                                { label: 'payment_intent.created', text: 'Fired when an intent is registered' },
                                { label: 'payment_intent.succeeded', text: 'Fired after manual confirmation' },
                                { label: 'payment_intent.payment_failed', text: 'Fired on decline or timeout' },
                                { label: 'payment_intent.refunded', text: 'Fired when a refund is applied' },
                            ]}
                            keyFacts={[
                                { label: 'Real-time events', value: 'payment_intent.*' },
                                { label: 'Echo sync', value: 'Built-in' },
                                { label: 'Retry logic', value: 'Testable' },
                            ]}
                            cta="Open Webhook Tester" ctaTo="/dev-hub/webhook-tester"
                            ac={A} ab={AB} index={1}
                        />
                    </div>
                </section>

                {/* How it works — white section */}
                <section className="bg-white py-20">
                    <div className="max-w-7xl mx-auto px-6">
                        <SH icon={Code2} ic={A} ib={AB}
                            eyebrow="Integration"
                            title="Three steps to test"
                            subtitle="Point any backend at your private sandbox URL and simulate real payment outcomes — no gateway keys required."
                        />
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            {[
                                { step: '01', title: 'Copy your endpoint',
                                  desc: 'Generate a free sandbox and get a unique Payment API URL. Use it in your checkout integration exactly like a real gateway endpoint.' },
                                { step: '02', title: 'Create a payment intent',
                                  desc: 'POST a payment intent from your backend. The sandbox captures it, queues a webhook, and waits for your manual confirmation trigger.' },
                                { step: '03', title: 'Simulate any outcome',
                                  desc: 'Use the dashboard to Succeed, Decline, or Delay the intent. Watch your app respond to every lifecycle event in real time.' },
                            ].map((s, i) => (
                                <m.div key={i} {...fu(i * 0.1)} className="bg-white border border-[#e8eaed] rounded-[32px] p-8">
                                    <div className="text-[2rem] font-light mb-4" style={{ color: A }}>{s.step}</div>
                                    <h3 className="text-xl font-medium text-[#121317] mb-3">{s.title}</h3>
                                    <p className="text-[#5f6368] leading-relaxed">{s.desc}</p>
                                </m.div>
                            ))}
                        </div>
                        <m.div {...fu(0.25)} className="mt-8 rounded-[24px] p-5 flex gap-3" style={{ backgroundColor: AB, color: AD }}>
                            <Info size={18} className="shrink-0 mt-0.5" />
                            <p className="m-0 text-[0.9375rem] leading-relaxed">
                                <strong>Full lifecycle support:</strong> 3D Secure, partial refunds, dispute simulations, and network timeout scenarios are all available on demand.
                            </p>
                        </m.div>
                    </div>
                </section>

                {/* Why — white section */}
                <WhySection />

                {/* Platform Qualities — fafafa */}
                <PlatformQualities />

                {/* Bottom CTA — fafafa */}
                <BottomCTA />
            </div>
        );
    }

    // ── Dashboard view ────────────────────────────────────────────────────────
    return (
        <div className="min-h-screen bg-white font-brand text-[#202124]">
            <SEO title="Payment Simulator | FileFusion Developer Hub" description="Test your payment integration with a full-lifecycle sandbox." />

            {/* Dashboard hero */}
            <header className="max-w-7xl mx-auto px-6 pt-16 pb-12 text-center border-b border-[#f1f3f4]">
                <m.div {...fu(0)}>
                    <Typography variant="hero-title" as="h1" weight="light" className="mb-6 text-[#121317]">
                        Your payment sandbox.{' '}<span style={{ color: A }}>Ready to test.</span>
                    </Typography>
                    <Typography variant="hero-subtitle" weight="normal" className="max-w-2xl mx-auto text-[#5f6368] mb-10">
                        Dispatch intents, trigger outcomes, and watch your webhook handler respond — all in real time.
                    </Typography>
                    <div className="flex flex-wrap gap-4 justify-center">
                        <button
                            className={`inline-flex items-center gap-2 px-7 py-3 font-medium rounded-full text-white transition-all shadow-sm cursor-pointer border-none relative overflow-hidden group ${dispatchStatus === 'success' ? 'bg-[#1e8e3e]' : dispatchStatus === 'error' ? 'bg-[#d93025]' : ''}`}
                            style={!dispatchStatus ? { backgroundColor: A } : {}}
                            onClick={sendTest} disabled={isSendingTest}>
                            {isSendingTest ? <RefreshCw size={17} className="animate-spin" /> : dispatchStatus === 'success' ? <Check size={17} /> : dispatchStatus === 'error' ? <Info size={17} /> : <Send size={17} />}
                            {isSendingTest ? 'Dispatching…' : dispatchStatus === 'success' ? 'Sent!' : dispatchStatus === 'error' ? 'Failed' : 'Dispatch Test Payment'}
                            <div className="absolute top-0 left-[-100%] w-full h-full bg-gradient-to-r from-transparent via-white/20 to-transparent transition-all duration-500 group-hover:left-[100%]" />
                        </button>
                    </div>
                </m.div>
            </header>

            {/* Intents + Inspector */}
            <section className="max-w-7xl mx-auto px-6 py-14">
                <SH icon={CreditCard} ic={A} ib={AB}
                    eyebrow="Sandbox Activity"
                    title="Recent intents & inspector"
                    subtitle="Select a transaction to inspect its details, trigger an outcome, and watch your webhook handler respond."
                />
                <m.div {...fu(0)} className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-6 items-start">

                    {/* Sidebar */}
                    <div className={`${CARD} h-[560px] flex flex-col sticky top-20 z-10`}>
                        <div className="px-6 py-5 border-b border-[#e8eaed] flex justify-between items-center">
                            <h4 className="text-xl font-medium text-[#121317]">Recent Intents</h4>
                            <div className="flex items-center gap-2">
                                <div className={`px-2.5 py-1 rounded-full text-[0.6875rem] font-bold uppercase tracking-wide ${isPolling ? 'bg-[#e6f4ea] text-[#1e8e3e]' : 'bg-[#fce8e6] text-[#d93025]'}`}>
                                    {isPolling ? 'LIVE' : 'PAUSED'}
                                </div>
                                <button className={`w-8 h-8 rounded-xl flex items-center justify-center cursor-pointer border-none ${!isPolling ? 'bg-[#fef7e0] text-[#b45309]' : 'bg-transparent text-[#1a73e8] hover:bg-[#e8eaed]'}`}
                                    onClick={() => setIsPolling(p => !p)} title={isPolling ? 'Pause' : 'Resume'}>
                                    {isPolling ? <Pause size={14} /> : <Play size={14} />}
                                </button>
                                <button className="w-8 h-8 rounded-xl flex items-center justify-center cursor-pointer border-none bg-transparent text-[#d93025] hover:bg-[#fce8e6]"
                                    onClick={clearHistory} title="Clear history">
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        </div>
                        <div className="px-4 py-3 border-b border-[#e8eaed] flex items-center gap-2 bg-[#fafafa]">
                            <Search size={14} className="text-[#80868b] shrink-0" />
                            <input type="text" className="w-full bg-transparent border-none outline-none text-[0.875rem] placeholder-[#80868b]"
                                placeholder="Search intents…" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                        </div>
                        <div className="flex-1 overflow-y-auto min-h-0">
                            {filtered.length === 0 ? (
                                <div className="flex flex-col items-center justify-center h-full p-10 text-center">
                                    <CreditCard size={36} className="mb-3" style={{ color: '#e8eaed' }} strokeWidth={1.5} />
                                    <p className="text-[0.9rem] text-[#5f6368] font-medium m-0">
                                        {searchTerm ? 'No matches found.' : 'No transactions yet.'}
                                    </p>
                                    {!searchTerm && <p className="text-[0.8rem] text-[#80868b] mt-1 m-0">Click "Dispatch Test Payment" above</p>}
                                </div>
                            ) : filtered.map(intent => (
                                <div key={intent.id}
                                    className={`px-6 py-4 border-b border-[#e8eaed] cursor-pointer transition-all duration-200 hover:bg-[#f8f9fa] border-l-4 ${activeIntent?.id === intent.id ? 'border-l-[#f9ab00] bg-[#fef7e0]' : 'border-l-transparent'}`}
                                    onClick={() => setActiveIntent(intent)}>
                                    <div className="flex justify-between items-center mb-1">
                                        <span className="font-medium text-[0.875rem] text-[#121317] truncate">{intent.id}</span>
                                        <span className="text-[0.75rem] text-[#80868b] ml-auto shrink-0 font-medium tabular-nums">{new Date(intent.created * 1000).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <div className="text-[0.9rem] font-semibold" style={{ color: AD }}>{fmt(intent.amount, intent.currency)}</div>
                                        <DevStatusPill status={statusVariant(intent.status)} className="text-[0.625rem]">{intent.status}</DevStatusPill>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Inspector */}
                    <div className={`${CARD} min-h-[560px]`}>
                        {activeIntent ? (
                            <>
                                <div className="px-8 py-5 border-b border-[#e8eaed] flex justify-between items-center">
                                    <h4 className="text-xl font-medium text-[#121317]">Payment Inspector</h4>
                                    <DevStatusPill status={statusVariant(activeIntent.status)}>{activeIntent.status}</DevStatusPill>
                                </div>
                                <div className="p-8 md:p-10">
                                    <div className="mb-8">
                                        <h2 className="text-4xl font-light text-[#121317] mb-1">{fmt(activeIntent.amount, activeIntent.currency)}</h2>
                                        <div className="text-[0.875rem] text-[#80868b] font-mono">{activeIntent.id}</div>
                                    </div>

                                    {/* Controls */}
                                    <div className="border-t border-[#e8eaed] pt-8 mb-8">
                                        <div className="flex items-center gap-3 mb-2">
                                            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: AB, color: A }}>
                                                <Zap size={16} strokeWidth={1.5} />
                                            </div>
                                            <h5 className="text-[0.9375rem] font-medium text-[#121317] m-0">Simulator Controls</h5>
                                        </div>
                                        <p className="text-[0.875rem] text-[#5f6368] mb-5 leading-relaxed ml-12">Manually trigger a payment outcome to test your app's response logic.</p>
                                        <div className="flex gap-3 flex-wrap ml-12">
                                            <button
                                                disabled={confirming || activeIntent.status !== 'requires_confirmation'}
                                                onClick={() => handleConfirm('succeeded')}
                                                className="inline-flex items-center gap-2 px-5 py-2 rounded-full font-medium text-white text-[0.875rem] border-none cursor-pointer transition-all hover:opacity-90 hover:shadow-md disabled:opacity-40 disabled:cursor-not-allowed"
                                                style={{ backgroundColor: '#1e8e3e' }}>
                                                <ShieldCheck size={15} /> Succeed
                                            </button>
                                            <button
                                                disabled={confirming || activeIntent.status !== 'requires_confirmation'}
                                                onClick={() => handleConfirm('declined', 500)}
                                                className="inline-flex items-center gap-2 px-5 py-2 rounded-full font-medium text-white text-[0.875rem] border-none cursor-pointer transition-all hover:opacity-90 hover:shadow-md disabled:opacity-40 disabled:cursor-not-allowed"
                                                style={{ backgroundColor: '#d93025' }}>
                                                <AlertTriangle size={15} /> Decline
                                            </button>
                                            <button
                                                disabled={confirming || activeIntent.status !== 'requires_confirmation'}
                                                onClick={() => handleConfirm('processing', 2000)}
                                                className="inline-flex items-center gap-2 px-5 py-2 rounded-full font-medium text-[0.875rem] border border-[#dadce0] bg-white text-[#3c4043] cursor-pointer transition-all hover:bg-[#f8f9fa] disabled:opacity-40 disabled:cursor-not-allowed">
                                                <RefreshCw size={15} className={confirming ? 'animate-spin' : ''} /> Test Latency (2s)
                                            </button>
                                        </div>
                                    </div>

                                    {/* Webhook */}
                                    <div className="border-t border-[#e8eaed] pt-8 mb-8">
                                        <div className="flex justify-between items-center mb-3">
                                            <div className="flex items-center gap-3">
                                                <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: AB, color: A }}>
                                                    <Webhook size={16} strokeWidth={1.5} />
                                                </div>
                                                <h5 className="text-[0.9375rem] font-medium text-[#121317] m-0">Webhook Status</h5>
                                            </div>
                                            {activeIntent.webhook_url && (
                                                <div className="flex gap-2 items-center">
                                                    <button className={`w-8 h-8 flex items-center justify-center rounded-xl cursor-pointer border-none ${copiedInspectorUrl ? 'bg-[#e6f4ea] text-[#1e8e3e]' : 'bg-transparent text-[#5f6368] hover:bg-[#e8eaed]'}`}
                                                        onClick={() => { navigator.clipboard.writeText(activeIntent.webhook_url!); setCopiedInspectorUrl(true); setTimeout(() => setCopiedInspectorUrl(false), 2000); }}>
                                                        {copiedInspectorUrl ? <Check size={15} /> : <Copy size={15} />}
                                                    </button>
                                                    <a href="/dev-hub/webhook-tester"
                                                        target="_blank" rel="noopener noreferrer"
                                                        onClick={(e) => { e.preventDefault(); window.open('/dev-hub/webhook-tester', '_blank'); }}
                                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[0.75rem] font-medium text-white no-underline hover:opacity-90 cursor-pointer"
                                                        style={{ backgroundColor: A }}>
                                                        <ExternalLink size={12} /> Webhook Logs
                                                    </a>
                                                </div>
                                            )}
                                        </div>
                                        <div className="ml-12 bg-[#f8f9fa] border border-[#e8eaed] rounded-2xl p-4">
                                            <span className="text-[0.875rem] text-[#5f6368]">
                                                {activeIntent.webhook_url ? (
                                                    activeIntent.status === 'requires_confirmation'
                                                        ? <><span>Waiting for confirmation to ping:</span>{' '}<code className="text-[0.8em] font-mono text-[#121317] bg-white border border-[#e8eaed] px-1.5 py-0.5 rounded-lg">{activeIntent.webhook_url}</code></>
                                                        : <><span>Webhook delivered (200 OK) to:</span>{' '}<code className="text-[0.8em] font-mono text-[#121317] bg-white border border-[#e8eaed] px-1.5 py-0.5 rounded-lg">{activeIntent.webhook_url}</code></>
                                                ) : 'No webhook URL provided.'}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Timeline */}
                                    <div className="border-t border-[#e8eaed] pt-8 mb-8">
                                        <div className="flex items-center gap-3 mb-5">
                                            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: AB, color: A }}>
                                                <Clock size={16} strokeWidth={1.5} />
                                            </div>
                                            <h5 className="text-[0.9375rem] font-medium text-[#121317] m-0">Transaction Timeline</h5>
                                        </div>
                                        <div className="relative pl-6 border-l-2 border-[#e8eaed] ml-12">
                                            {activeIntent.timeline?.map((ev, idx) => (
                                                <div key={idx} className="mb-5 relative">
                                                    <div className="absolute left-[-31px] top-1 w-3 h-3 rounded-full border-2 border-white shadow-[0_0_0_2px_#f8f9fa]"
                                                        style={{ 
                                                            background: 
                                                                ev.event === 'created' ? A : 
                                                                (ev.event === 'declined' || ev.event === 'failed' || ev.event === 'error' || ev.message.toLowerCase().includes('decline') || ev.message.toLowerCase().includes('fail') || ev.message.toLowerCase().includes('reject') || ev.message.toLowerCase().includes('error')) ? '#d93025' : 
                                                                (ev.event === 'processing' || ev.event === 'latency' || ev.event === 'delay' || ev.message.toLowerCase().includes('processing') || ev.message.toLowerCase().includes('latency') || ev.message.toLowerCase().includes('delay')) ? '#1a73e8' : 
                                                                (ev.event === 'confirmed' || ev.event === 'succeeded' || ev.message.toLowerCase().includes('succeed') || ev.message.toLowerCase().includes('confirm')) ? '#1e8e3e' : 
                                                                '#80868b' 
                                                        }} />
                                                    <div className="text-[0.875rem] font-medium text-[#121317]">{ev.message}</div>
                                                    <div className="text-[0.75rem] text-[#80868b] mt-0.5">{new Date(ev.ts * 1000).toLocaleString()}</div>
                                                </div>
                                            )) || <div className="text-[0.875rem] text-[#80868b]">Timeline data not available.</div>}
                                        </div>
                                    </div>

                                    {/* Metadata */}
                                    <div className="border-t border-[#e8eaed] pt-8">
                                        <div className="flex justify-between items-center mb-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: AB, color: A }}>
                                                    <Info size={16} strokeWidth={1.5} />
                                                </div>
                                                <h5 className="text-[0.9375rem] font-medium text-[#121317] m-0">Payload Metadata</h5>
                                            </div>
                                            <button className={`w-8 h-8 flex items-center justify-center rounded-xl cursor-pointer border-none ${copiedMetadata ? 'bg-[#e6f4ea] text-[#1e8e3e]' : 'bg-transparent text-[#5f6368] hover:bg-[#e8eaed]'}`}
                                                onClick={() => {
                                                    const o = { description: activeIntent.description, metadata: activeIntent.metadata, created_at: new Date(activeIntent.created * 1000).toISOString() };
                                                    navigator.clipboard.writeText(JSON.stringify(o, null, 2));
                                                    setCopiedMetadata(true); setTimeout(() => setCopiedMetadata(false), 2000);
                                                }}>
                                                {copiedMetadata ? <Check size={15} /> : <Copy size={15} />}
                                            </button>
                                        </div>
                                        <div className="ml-12 bg-[#f8f9fa] border border-[#e8eaed] rounded-2xl p-4">
                                            <CodeBlock language="json"
                                                code={JSON.stringify({ description: activeIntent.description, metadata: activeIntent.metadata, created_at: new Date(activeIntent.created * 1000).toISOString() }, null, 2)}
                                                variant="light" showLanguage={false} showCopy={false} className="!my-0 !border-none !shadow-none !bg-transparent" />
                                        </div>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="h-[560px] flex flex-col items-center justify-center p-12 text-center">
                                <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-6" style={{ backgroundColor: AB, color: A }}>
                                    <CreditCard size={24} strokeWidth={1.5} />
                                </div>
                                <h4 className="text-[0.75rem] font-semibold uppercase tracking-widest mb-4" style={{ color: A }}>Inspector</h4>
                                <p className="text-[0.9375rem] text-[#5f6368] leading-[1.75] max-w-xs m-0">
                                    Select a transaction from the list to inspect its details and simulate an outcome.
                                </p>
                            </div>
                        )}
                    </div>
                </m.div>
            </section>

            {/* Implementation Snippets */}
            <section className="max-w-7xl mx-auto px-6 py-14 border-t border-[#f1f3f4]">
                <SH icon={Code2} ic={A} ib={AB}
                    eyebrow="Integration"
                    title="Get Started in Minutes"
                    subtitle="Copy your sandbox endpoint and start dispatching payment intents from your backend immediately."
                />

                <m.div {...fu(0)} className="border-t border-[#e8eaed] pt-10 mb-10">
                    <div className="flex items-start gap-4 mb-8">
                        <div className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 mt-0.5" style={{ backgroundColor: AB, color: A }}>
                            <Zap size={24} strokeWidth={1.5} />
                        </div>
                        <div>
                            <h3 className="text-2xl font-medium text-[#121317] leading-snug">Implementation Snippets</h3>
                            <p className="text-[0.9rem] mt-1" style={{ color: A }}>Implement, test, and dispatch payment intents</p>
                        </div>
                    </div>
                    <div className="ml-12">
                        <p className="text-[0.75rem] font-semibold uppercase tracking-widest text-[#80868b] mb-4">Implement in your code</p>
                        <div className="bg-[#f8f9fa] border border-[#e8eaed] rounded-2xl overflow-hidden max-w-5xl">
                            <div className="flex justify-between items-center pr-3 pl-4 border-b border-[#e8eaed]">
                                <div className="flex">
                                    {(['javascript', 'python', 'nodejs'] as const).map(lang => (
                                        <button key={lang}
                                            className={`px-4 py-3 bg-transparent border-none border-b-2 text-[0.75rem] font-semibold uppercase tracking-wide cursor-pointer transition-all duration-200 ${activeTab === lang ? 'border-b-[#f9ab00]' : 'border-transparent text-[#80868b] hover:text-[#121317]'}`}
                                            style={activeTab === lang ? { color: A } : {}}
                                            onClick={() => setActiveTab(lang)}>{lang}
                                        </button>
                                    ))}
                                </div>
                                <button
                                    className={`w-8 h-8 flex items-center justify-center rounded-xl cursor-pointer transition-colors duration-200 border-none ${copiedSnippet ? 'bg-[#e6f4ea] text-[#1e8e3e]' : 'bg-transparent text-[#5f6368] hover:bg-[#e8eaed] hover:text-[#121317]'}`}
                                    onClick={() => { navigator.clipboard.writeText(getSnippet(activeTab)); setCopiedSnippet(true); setTimeout(() => setCopiedSnippet(false), 2000); }}>
                                    {copiedSnippet ? <Check size={15} /> : <Copy size={15} />}
                                </button>
                            </div>
                            <div className="h-72 overflow-y-auto [&_pre]:!whitespace-pre-wrap [&_pre]:!break-all [&_code]:!whitespace-pre-wrap [&_.code-block-container]:m-0 [&_.code-block-container]:border-none [&_.code-block-container]:shadow-none [&_.code-block-container]:rounded-none">
                                <CodeBlock language={activeTab === 'nodejs' ? 'javascript' : activeTab} code={getSnippet(activeTab)} variant="light" showLanguage={false} showCopy={false} className="!my-0 !rounded-none !border-none !shadow-none" />
                            </div>
                        </div>
                    </div>
                </m.div>

                {/* Sandbox URL */}
                <m.div {...fu(0.05)} className="border-t border-[#e8eaed] pt-10">
                    <div className="flex items-start gap-4 mb-8">
                        <div className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 mt-0.5" style={{ backgroundColor: AB, color: A }}>
                            <ExternalLink size={22} strokeWidth={1.5} />
                        </div>
                        <div>
                            <h3 className="text-2xl font-medium text-[#121317] leading-snug">Your Unique Sandbox URL</h3>
                            <p className="text-[0.9rem] mt-1" style={{ color: A }}>Point your payment client here to capture intents</p>
                        </div>
                    </div>
                    <div className="ml-12">
                        <div className="flex items-stretch bg-[#f8f9fa] border border-[#e8eaed] rounded-2xl overflow-hidden max-w-5xl hover:border-[#f9ab00] hover:shadow-[0_4px_12px_rgba(249,171,0,0.08)] transition-all duration-200">
                            <div className="px-4 py-3 font-bold text-[0.75rem] border-r border-[#e8eaed] flex items-center min-w-[72px]" style={{ backgroundColor: AB, color: AD }}>POST</div>
                            <div className="flex-1 px-4 py-3 font-mono text-[0.8rem] text-[#5f6368] break-all flex items-center">{createUrl}</div>
                            <button
                                className={`w-12 flex items-center justify-center cursor-pointer border-none ${copiedUrl ? 'bg-[#e6f4ea] text-[#1e8e3e]' : 'bg-transparent text-[#5f6368] hover:bg-[#e8eaed] hover:text-[#121317]'}`}
                                onClick={() => { navigator.clipboard.writeText(createUrl); setCopiedUrl(true); setTimeout(() => setCopiedUrl(false), 2000); }}>
                                {copiedUrl ? <Check size={15} /> : <Copy size={15} />}
                            </button>
                        </div>
                    </div>
                </m.div>
            </section>

            <PlatformQualities />
        </div>
    );
};

export default PaymentMockPage;
